import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getCMSDState, setCMSDState, setCustomHeaders } from '../../api/cmsd';

function CMSDControls({ pipeline }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customHeaderKey, setCustomHeaderKey] = useState('');
  const [customHeaderValue, setCustomHeaderValue] = useState('');
  const [copied, setCopied] = useState(null);

  // Only show for pipelines with proxy_url configured
  if (!pipeline.proxy_url) {
    return null;
  }

  const fetchState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const cmsdState = await getCMSDState(pipeline.id);
      setState(cmsdState);
    } catch (err) {
      console.error('Failed to fetch CMSD state:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pipeline.id]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // Poll every 10 seconds if demo-scenario is active
  useEffect(() => {
    if (state?.state !== 'demo-scenario') return;

    const interval = setInterval(() => {
      fetchState();
    }, 10000);

    return () => clearInterval(interval);
  }, [state?.state, fetchState]);

  const handleStateChange = async (event, newState) => {
    if (!newState) return;
    try {
      setLoading(true);
      setError(null);
      await setCMSDState(pipeline.id, newState);
      await fetchState();
    } catch (err) {
      console.error('Failed to set CMSD state:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomHeader = async () => {
    if (!customHeaderKey || !customHeaderValue) return;
    try {
      setLoading(true);
      const newHeaders = {
        ...state.custom_headers,
        [customHeaderKey]: customHeaderValue,
      };
      await setCustomHeaders(pipeline.id, newHeaders);
      setCustomHeaderKey('');
      setCustomHeaderValue('');
      await fetchState();
    } catch (err) {
      console.error('Failed to add custom header:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveHeader = async (key) => {
    try {
      setLoading(true);
      const newHeaders = { ...state.custom_headers };
      delete newHeaders[key];
      await setCustomHeaders(pipeline.id, newHeaders);
      await fetchState();
    } catch (err) {
      console.error('Failed to remove custom header:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStateBadgeColor = () => {
    if (!state) return 'default';
    switch (state.effective_state) {
      case 'good':
        return 'success';
      case 'bad':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && !state) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        mt: 2,
        background: isDark
          ? 'rgba(0, 150, 255, 0.05)'
          : 'rgba(0, 100, 200, 0.02)',
        border: isDark
          ? '1px solid rgba(0, 150, 255, 0.2)'
          : '1px solid rgba(0, 100, 200, 0.1)',
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography
            variant="subtitle2"
            sx={{
              color: isDark ? '#64b5f6' : '#1976d2',
              fontWeight: 600,
            }}
          >
            CMSD Header Injection
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchState} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* State Toggle */}
        <Box mb={2}>
          <Typography variant="caption" color="textSecondary" gutterBottom display="block">
            State
          </Typography>
          <ToggleButtonGroup
            value={state?.state || 'good'}
            exclusive
            onChange={handleStateChange}
            disabled={loading}
            size="small"
            fullWidth
          >
            <ToggleButton value="good" color="success">
              Good
            </ToggleButton>
            <ToggleButton value="bad" color="error">
              Bad
            </ToggleButton>
            <ToggleButton value="demo-scenario" color="warning">
              Demo Scenario
            </ToggleButton>
          </ToggleButtonGroup>

          {state?.state === 'demo-scenario' && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="textSecondary">
                Currently:
              </Typography>
              <Chip
                label={state.effective_state}
                color={getStateBadgeColor()}
                size="small"
              />
              <Typography variant="caption" color="textSecondary">
                (10m good / 4m bad cycle)
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Current Headers Display */}
        <Box mb={2}>
          <Typography variant="caption" color="textSecondary" gutterBottom display="block">
            Current Headers
          </Typography>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              p: 1.5,
              bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
              borderRadius: 1,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all' }}>
                <strong>CMSD-Static:</strong> {state?.static_header || 'Not set'}
              </Typography>
              {state?.static_header && (
                <Tooltip title={copied === 'static' ? 'Copied!' : 'Copy'}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(state.static_header, 'static')}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all' }}>
                <strong>CMSD-Dynamic:</strong> {state?.dynamic_header || 'Not set'}
              </Typography>
              {state?.dynamic_header && (
                <Tooltip title={copied === 'dynamic' ? 'Copied!' : 'Copy'}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(state.dynamic_header, 'dynamic')}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Custom Headers */}
        <Box>
          <Typography variant="caption" color="textSecondary" gutterBottom display="block">
            Custom Headers
          </Typography>

          <Box display="flex" gap={1} mb={1}>
            <TextField
              size="small"
              placeholder="Header Name"
              value={customHeaderKey}
              onChange={(e) => setCustomHeaderKey(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              placeholder="Value"
              value={customHeaderValue}
              onChange={(e) => setCustomHeaderValue(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddCustomHeader}
              disabled={!customHeaderKey || !customHeaderValue || loading}
            >
              Add
            </Button>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {state?.custom_headers && Object.entries(state.custom_headers).map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                onDelete={() => handleRemoveHeader(key)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* Proxy URL Display */}
        <Box mt={2} pt={1} borderTop={`1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`}>
          <Typography variant="caption" color="textSecondary">
            Proxy URL: <code>{pipeline.proxy_url}</code>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default CMSDControls;
